import { useEffect, useState, useRef } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { Trash01Icon, PlusIcon, XCloseIcon } from '@untitledui-icons/react/line'
import { Modal, ModalContent, ModalHeader, ModalFooter } from '@/components/ui/Modal'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import { TextArea } from '@/components/base/textarea/textarea'
import { Checkbox } from '@/components/base/checkbox/checkbox'
import { Select } from '@/components/ui/Select'
import { useAIDeployments } from '@/hooks/api/useAIDeployments'
import { useSimTags, useCreateTag } from '@/hooks/api/useSimulations'
import { cn } from '@/lib/utils'
import type { SimScenario, SimScenarioInput, MockResponses } from '@/types/simulation'
import { TemplateReferencePanel } from './TemplateReferencePanel'

const scenarioSchema = z.object({
  name: z.string().min(1, 'pages.simulations.validation.name_required'),
  comment: z.string().optional(),
  tag_ids: z.array(z.number()).default([]),
  scenario_prompt: z.string().min(1, 'pages.simulations.validation.prompt_required'),
  expected_tools: z.string().optional(),
  must_contain: z.string().optional(),
  must_not_contain: z.string().optional(),
  llm_judge_criteria: z.string().optional(),
  max_tool_param_errors: z.number().min(0).default(0),
  skip_pre_call_handlers: z.boolean().default(false),
  pre_call_prompt_variables: z.string().optional(),
  pre_call_prepend_messages: z.string().optional(),
  run_post_call_tools: z.boolean().default(false),
  expected_post_call_tools: z.string().optional(),
  max_turns: z.number().min(1).max(50).default(10),
  simulator_ai_deployment_id: z.number().nullable().optional(),
  judge_ai_deployment_id: z.number().nullable().optional(),
  priority: z.number().min(0).default(0),
})

/**
 * State for a single mock response entry in the form
 * All mock responses now use Jinja2 templating by default
 */
interface MockEntry {
  id: string
  toolName: string
  response: string
}

type FormData = z.infer<typeof scenarioSchema>

interface ScenarioFormDialogProps {
  isOpen: boolean
  onClose: () => void
  onSubmit: (data: SimScenarioInput) => Promise<void>
  scenario?: SimScenario | null
  isClone?: boolean
  isLoading?: boolean
}

/**
 * Dialog for creating/editing simulation scenarios
 */
export function ScenarioFormDialog({
  isOpen,
  onClose,
  onSubmit,
  scenario,
  isClone = false,
  isLoading = false,
}: ScenarioFormDialogProps) {
  const { t } = useTranslation()
  const isEdit = !!scenario && !isClone

  // State for mock responses (managed separately from react-hook-form)
  const [mockEntries, setMockEntries] = useState<MockEntry[]>([])
  // State for post-call mock responses
  const [postCallMockEntries, setPostCallMockEntries] = useState<MockEntry[]>([])
  // State for tag autocomplete
  const [tagInputValue, setTagInputValue] = useState('')
  const [isTagDropdownOpen, setIsTagDropdownOpen] = useState(false)
  const tagInputRef = useRef<HTMLInputElement>(null)
  const tagDropdownRef = useRef<HTMLDivElement>(null)

  // Fetch available AI deployments
  const { data: aiDeployments = [] } = useAIDeployments()
  // Fetch available tags
  const { data: availableTags = [] } = useSimTags()
  const createTag = useCreateTag()

  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(scenarioSchema),
    defaultValues: {
      name: '',
      comment: '',
      tag_ids: [],
      scenario_prompt: '',
      expected_tools: '',
      must_contain: '',
      must_not_contain: '',
      llm_judge_criteria: '',
      max_tool_param_errors: 0,
      skip_pre_call_handlers: false,
      pre_call_prompt_variables: '',
      pre_call_prepend_messages: '',
      run_post_call_tools: false,
      expected_post_call_tools: '',
      max_turns: 10,
      simulator_ai_deployment_id: null,
      judge_ai_deployment_id: null,
      priority: 0,
    },
  })

  const selectedTagIds = watch('tag_ids') ?? []

  // Reset form when scenario changes
  useEffect(() => {
    if (scenario) {
      reset({
        name: scenario.name,
        comment: scenario.comment ?? '',
        tag_ids: scenario.tags?.map((tag) => tag.id) ?? [],
        scenario_prompt: scenario.scenario_prompt,
        expected_tools: parseArrayField(scenario.expected_tools),
        must_contain: parseArrayField(scenario.must_contain),
        must_not_contain: parseArrayField(scenario.must_not_contain),
        llm_judge_criteria: parseStringField(scenario.llm_judge_criteria),
        max_tool_param_errors: scenario.max_tool_param_errors ?? 0,
        skip_pre_call_handlers: scenario.skip_pre_call_handlers ?? false,
        pre_call_prompt_variables: parseJsonField(scenario.pre_call_prompt_variables),
        pre_call_prepend_messages: parseJsonField(scenario.pre_call_prepend_messages),
        run_post_call_tools: scenario.run_post_call_tools ?? false,
        expected_post_call_tools: parseArrayField(scenario.expected_post_call_tools),
        max_turns: scenario.max_turns,
        simulator_ai_deployment_id: scenario.simulator_ai_deployment_id ?? null,
        judge_ai_deployment_id: scenario.judge_ai_deployment_id ?? null,
        priority: scenario.priority ?? 0,
      })
      // Parse mock_responses into entries
      setMockEntries(parseMockResponses(scenario.mock_responses))
      // Parse post_call_mock_responses into entries
      setPostCallMockEntries(parseMockResponses(scenario.post_call_mock_responses))
    } else {
      reset({
        name: '',
        comment: '',
        tag_ids: [],
        scenario_prompt: '',
        expected_tools: '',
        must_contain: '',
        must_not_contain: '',
        llm_judge_criteria: '',
        max_tool_param_errors: 0,
        skip_pre_call_handlers: false,
        pre_call_prompt_variables: '',
        pre_call_prepend_messages: '',
        run_post_call_tools: false,
        expected_post_call_tools: '',
        max_turns: 10,
        simulator_ai_deployment_id: null,
        judge_ai_deployment_id: null,
        priority: 0,
      })
      setMockEntries([])
      setPostCallMockEntries([])
    }
    setTagInputValue('')
    setIsTagDropdownOpen(false)
  }, [scenario, reset])

  const handleFormSubmit = async (data: FormData) => {
    const input: SimScenarioInput = {
      name: data.name,
      comment: data.comment || null,
      tag_ids: data.tag_ids,
      scenario_prompt: data.scenario_prompt,
      expected_tools: parseCommaSeparated(data.expected_tools),
      must_contain: parseCommaSeparated(data.must_contain),
      must_not_contain: parseCommaSeparated(data.must_not_contain),
      llm_judge_criteria: data.llm_judge_criteria || null,
      mock_responses: buildMockResponses(mockEntries),
      max_tool_param_errors: data.max_tool_param_errors,
      skip_pre_call_handlers: data.skip_pre_call_handlers,
      pre_call_prompt_variables: parseJsonString(data.pre_call_prompt_variables),
      pre_call_prepend_messages: parseJsonArrayString(data.pre_call_prepend_messages),
      run_post_call_tools: data.run_post_call_tools,
      post_call_mock_responses: buildMockResponses(postCallMockEntries),
      expected_post_call_tools: parseCommaSeparated(data.expected_post_call_tools),
      max_turns: data.max_turns,
      simulator_ai_deployment_id: data.simulator_ai_deployment_id ?? null,
      judge_ai_deployment_id: data.judge_ai_deployment_id ?? null,
      priority: data.priority,
    }

    await onSubmit(input)
    onClose()
  }

  // Tag handlers
  const handleAddTag = (tagId: number) => {
    if (!selectedTagIds.includes(tagId)) {
      setValue('tag_ids', [...selectedTagIds, tagId])
    }
    setTagInputValue('')
    setIsTagDropdownOpen(false)
  }

  const handleRemoveTag = (tagId: number) => {
    setValue('tag_ids', selectedTagIds.filter((id) => id !== tagId))
  }

  const handleCreateTag = async () => {
    const trimmedName = tagInputValue.trim()
    if (!trimmedName) return
    try {
      const newTag = await createTag.mutateAsync({ name: trimmedName })
      setValue('tag_ids', [...selectedTagIds, newTag.id])
      setTagInputValue('')
      setIsTagDropdownOpen(false)
    } catch {
      // Error handling - tag might already exist
    }
  }

  // Get selected tags
  const selectedTags = availableTags.filter((tag) => selectedTagIds.includes(tag.id))
  
  // Filter unselected tags by input value
  const filteredTags = availableTags.filter((tag) => {
    if (selectedTagIds.includes(tag.id)) return false
    if (!tagInputValue.trim()) return true
    return tag.name.toLowerCase().includes(tagInputValue.toLowerCase())
  })

  // Check if we should show the "create new" option
  const showCreateOption = tagInputValue.trim() && 
    !availableTags.some((tag) => tag.name.toLowerCase() === tagInputValue.trim().toLowerCase())

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target as Node) &&
        tagInputRef.current &&
        !tagInputRef.current.contains(event.target as Node)
      ) {
        setIsTagDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Mock entry handlers
  const addMockEntry = () => {
    setMockEntries((prev) => [
      ...prev,
      { id: crypto.randomUUID(), toolName: '', response: '' },
    ])
  }

  const removeMockEntry = (id: string) => {
    setMockEntries((prev) => prev.filter((entry) => entry.id !== id))
  }

  const updateMockEntry = (id: string, field: keyof MockEntry, value: string) => {
    setMockEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry))
    )
  }

  // Post-call mock entry handlers
  const addPostCallMockEntry = () => {
    setPostCallMockEntries((prev) => [
      ...prev,
      { id: crypto.randomUUID(), toolName: '', response: '' },
    ])
  }

  const removePostCallMockEntry = (id: string) => {
    setPostCallMockEntries((prev) => prev.filter((entry) => entry.id !== id))
  }

  const updatePostCallMockEntry = (id: string, field: keyof MockEntry, value: string) => {
    setPostCallMockEntries((prev) =>
      prev.map((entry) => (entry.id === id ? { ...entry, [field]: value } : entry))
    )
  }

  // Build AI deployment options
  const aiDeploymentOptions = [
    { value: '', label: t('pages.simulations.scenarios.fields.ai_deployment_default') },
    ...aiDeployments.map((deployment) => ({
      value: String(deployment.id),
      label: deployment.friendly_name || `${deployment.model}`,
    })),
  ]


  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl">
      <ModalContent className="flex max-h-[calc(100vh-3rem)] flex-col">
        <form
          onSubmit={handleSubmit(handleFormSubmit)}
          className="flex min-h-0 flex-col"
        >
          <ModalHeader onClose={onClose}>
            {isClone
              ? t('pages.simulations.scenarios.clone_title')
              : isEdit
                ? t('pages.simulations.scenarios.edit_title')
                : t('pages.simulations.scenarios.create_title')}
          </ModalHeader>

          {/* Scrollable content area */}
          <div className="flex-1 overflow-y-auto overscroll-contain">
            <div className="space-y-4 p-6">
              {/* Name */}
              <div>
                <Controller
                  name="name"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t('pages.simulations.scenarios.fields.name')}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={t('pages.simulations.scenarios.placeholders.name')}
                      errorMessage={errors.name ? t(errors.name.message ?? '') : undefined}
                    />
                  )}
                />
              </div>

              {/* Comment */}
              <div>
                <Controller
                  name="comment"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label={t('pages.simulations.scenarios.fields.comment')}
                      value={field.value ?? ''}
                      onChange={(val) => field.onChange(val)}
                      autoResize
                      placeholder={t('pages.simulations.scenarios.placeholders.comment')}
                    />
                  )}
                />
              </div>

              {/* Tags */}
              <div>
                <label className="text-fg-primary mb-2 block text-sm font-medium">
                  {t('pages.simulations.scenarios.fields.tags')}
                </label>

                {/* Selected tags */}
                {selectedTags.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1">
                    {selectedTags.map((tag) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center gap-1 rounded-md bg-brand-50 px-2 py-0.5 text-sm text-brand-700 dark:bg-brand-950 dark:text-brand-300"
                      >
                        {tag.name}
                        <button
                          type="button"
                          onClick={() => handleRemoveTag(tag.id)}
                          className="text-brand-500 hover:text-brand-700 dark:hover:text-brand-200"
                        >
                          <XCloseIcon className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Tag autocomplete input */}
                <div className="relative">
                  <input
                    ref={tagInputRef}
                    type="text"
                    value={tagInputValue}
                    onChange={(e) => {
                      setTagInputValue(e.target.value)
                      setIsTagDropdownOpen(true)
                    }}
                    onFocus={() => setIsTagDropdownOpen(true)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        if (showCreateOption) {
                          handleCreateTag()
                        } else if (filteredTags.length > 0) {
                          handleAddTag(filteredTags[0].id)
                        }
                      }
                      if (e.key === 'Escape') {
                        setIsTagDropdownOpen(false)
                      }
                    }}
                    placeholder={t('pages.simulations.scenarios.placeholders.search_or_create_tag')}
                    className={cn(
                      'w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm',
                      'placeholder:text-gray-400',
                      'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
                      'dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100'
                    )}
                  />

                  {/* Dropdown */}
                  {isTagDropdownOpen && (filteredTags.length > 0 || showCreateOption) && (
                    <div
                      ref={tagDropdownRef}
                      className={cn(
                        'absolute z-10 mt-1 w-full rounded-lg border border-gray-200 bg-white shadow-lg',
                        'dark:bg-gray-900 dark:border-gray-700',
                        'max-h-48 overflow-auto'
                      )}
                    >
                      {/* Existing tags */}
                      {filteredTags.map((tag) => (
                        <button
                          key={tag.id}
                          type="button"
                          onClick={() => handleAddTag(tag.id)}
                          className={cn(
                            'w-full px-3 py-2 text-left text-sm',
                            'text-gray-900 dark:text-gray-100',
                            'hover:bg-gray-100 dark:hover:bg-gray-800',
                            'first:rounded-t-lg last:rounded-b-lg'
                          )}
                        >
                          {tag.name}
                        </button>
                      ))}

                      {/* Create new tag option */}
                      {showCreateOption && (
                        <button
                          type="button"
                          onClick={handleCreateTag}
                          disabled={createTag.isPending}
                          className={cn(
                            'w-full px-3 py-2 text-left text-sm',
                            'text-brand-600 dark:text-brand-400',
                            'hover:bg-gray-100 dark:hover:bg-gray-800',
                            'first:rounded-t-lg last:rounded-b-lg',
                            'flex items-center gap-2'
                          )}
                        >
                          <PlusIcon className="h-4 w-4" />
                          {t('pages.simulations.scenarios.create_tag', { name: tagInputValue.trim() })}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Template Reference Panel */}
              <TemplateReferencePanel />

              {/* Scenario Prompt */}
              <div>
                <Controller
                  name="scenario_prompt"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label={t('pages.simulations.scenarios.fields.scenario_prompt')}
                      value={field.value}
                      onChange={(val) => field.onChange(val)}
                      autoResize
                      placeholder={t('pages.simulations.scenarios.placeholders.scenario_prompt')}
                      hint={
                        errors.scenario_prompt
                          ? t(errors.scenario_prompt.message ?? '')
                          : t('pages.simulations.scenarios.help.supports_templates')
                      }
                    />
                  )}
                />
              </div>

              {/* Expected Tools */}
              <div>
                <Controller
                  name="expected_tools"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t('pages.simulations.scenarios.fields.expected_tools')}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={t('pages.simulations.scenarios.placeholders.expected_tools')}
                      description={t('pages.simulations.scenarios.help.comma_separated')}
                    />
                  )}
                />
              </div>

              {/* Must Contain */}
              <div>
                <Controller
                  name="must_contain"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t('pages.simulations.scenarios.fields.must_contain')}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={t('pages.simulations.scenarios.placeholders.must_contain')}
                      description={t('pages.simulations.scenarios.help.comma_separated')}
                    />
                  )}
                />
              </div>

              {/* Must Not Contain */}
              <div>
                <Controller
                  name="must_not_contain"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t('pages.simulations.scenarios.fields.must_not_contain')}
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder={t('pages.simulations.scenarios.placeholders.must_not_contain')}
                      description={t('pages.simulations.scenarios.help.comma_separated')}
                    />
                  )}
                />
              </div>

              {/* LLM Judge Criteria */}
              <div>
                <Controller
                  name="llm_judge_criteria"
                  control={control}
                  render={({ field }) => (
                    <TextArea
                      label={t('pages.simulations.scenarios.fields.llm_judge_criteria')}
                      value={field.value ?? ''}
                      onChange={(val) => field.onChange(val)}
                      autoResize
                      placeholder={t('pages.simulations.scenarios.placeholders.llm_judge_criteria')}
                    />
                  )}
                />
              </div>

              {/* Mock Responses */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <label className="text-fg-primary text-sm font-medium">
                      {t('pages.simulations.scenarios.fields.mock_responses')}
                    </label>
                    <p className="text-fg-tertiary text-xs">
                      {t('pages.simulations.scenarios.help.mock_responses')}{' '}
                      {t('pages.simulations.scenarios.help.supports_templates')}
                    </p>
                  </div>
                  <Button
                    type="button"
                    color="secondary"
                    size="sm"
                    onClick={addMockEntry}
                    iconLeading={PlusIcon}
                  >
                    {t('pages.simulations.scenarios.add_mock')}
                  </Button>
                </div>

                {mockEntries.length === 0 ? (
                  <p className="text-fg-tertiary rounded-lg border border-dashed p-4 text-center text-sm">
                    {t('pages.simulations.scenarios.no_mocks')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {mockEntries.map((entry) => (
                      <div
                        key={entry.id}
                        className="bg-bg-secondary rounded-lg border p-3"
                      >
                        <div className="mb-2 flex items-start gap-2">
                          <div className="flex-1">
                            <Input
                              value={entry.toolName}
                              onChange={(val) => updateMockEntry(entry.id, 'toolName', val)}
                              placeholder={t(
                                'pages.simulations.scenarios.placeholders.mock_tool_name'
                              )}
                              size="sm"
                            />
                          </div>
                          <Button
                            color="tertiary"
                            size="sm"
                            onClick={() => removeMockEntry(entry.id)}
                            aria-label={t('resources.generic.delete')}
                            iconLeading={Trash01Icon}
                          />
                        </div>
                        <TextArea
                          value={entry.response}
                          onChange={(val) => updateMockEntry(entry.id, 'response', val)}
                          placeholder={t('pages.simulations.scenarios.placeholders.mock_template')}
                          autoResize
                          hint={t('pages.simulations.scenarios.help.mock_template_hint')}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Max Tool Param Errors */}
              <div>
                <Controller
                  name="max_tool_param_errors"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t('pages.simulations.scenarios.fields.max_tool_param_errors')}
                      type="number"
                      value={String(field.value ?? 0)}
                      onChange={(val) => field.onChange(val ? Number(val) : 0)}
                      onBlur={field.onBlur}
                      description={t('pages.simulations.scenarios.help.max_tool_param_errors')}
                    />
                  )}
                />
              </div>

              {/* Pre-Call Handler Configuration */}
              <div className="border-border-secondary space-y-4 rounded-lg border p-4">
                <div>
                  <Controller
                    name="skip_pre_call_handlers"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        isSelected={field.value}
                        onChange={field.onChange}
                        label={t('pages.simulations.scenarios.fields.skip_pre_call_handlers')}
                        size="sm"
                      />
                    )}
                  />
                  <p className="text-fg-tertiary mt-1 text-xs">
                    {t('pages.simulations.scenarios.help.skip_pre_call_handlers')}
                  </p>
                </div>

                {/* Custom Prompt Variables - only show when skip_pre_call_handlers is enabled */}
                <Controller
                  name="skip_pre_call_handlers"
                  control={control}
                  render={({ field: skipField }) => (
                    skipField.value ? (
                      <>
                        <div>
                          <Controller
                            name="pre_call_prompt_variables"
                            control={control}
                            render={({ field }) => (
                              <TextArea
                                label={t('pages.simulations.scenarios.fields.pre_call_prompt_variables')}
                                value={field.value ?? ''}
                                onChange={(val) => field.onChange(val)}
                                autoResize
                                placeholder={t('pages.simulations.scenarios.placeholders.pre_call_prompt_variables')}
                                hint={`${t('pages.simulations.scenarios.help.pre_call_prompt_variables')} ${t('pages.simulations.scenarios.help.supports_templates')}`}
                              />
                            )}
                          />
                        </div>

                        <div>
                          <Controller
                            name="pre_call_prepend_messages"
                            control={control}
                            render={({ field }) => (
                              <TextArea
                                label={t('pages.simulations.scenarios.fields.pre_call_prepend_messages')}
                                value={field.value ?? ''}
                                onChange={(val) => field.onChange(val)}
                                autoResize
                                placeholder={t('pages.simulations.scenarios.placeholders.pre_call_prepend_messages')}
                                hint={`${t('pages.simulations.scenarios.help.pre_call_prepend_messages')} ${t('pages.simulations.scenarios.help.supports_templates')}`}
                              />
                            )}
                          />
                        </div>
                      </>
                    ) : null
                  )}
                />
              </div>

              {/* Post-Call Tools Section */}
              <div className="border-border-secondary space-y-4 rounded-lg border p-4">
                <div>
                  <Controller
                    name="run_post_call_tools"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        isSelected={field.value}
                        onChange={field.onChange}
                        label={t('pages.simulations.scenarios.fields.run_post_call_tools')}
                        size="sm"
                      />
                    )}
                  />
                  <p className="text-fg-tertiary mt-1 text-xs">
                    {t('pages.simulations.scenarios.help.run_post_call_tools')}
                  </p>
                </div>

                {/* Expected Post-Call Tools */}
                <div>
                  <Controller
                    name="expected_post_call_tools"
                    control={control}
                    render={({ field }) => (
                      <Input
                        label={t('pages.simulations.scenarios.fields.expected_post_call_tools')}
                        value={field.value ?? ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        placeholder={t('pages.simulations.scenarios.placeholders.expected_post_call_tools')}
                        description={t('pages.simulations.scenarios.help.expected_post_call_tools')}
                      />
                    )}
                  />
                </div>

                {/* Post-Call Mock Responses */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-fg-primary text-sm font-medium">
                        {t('pages.simulations.scenarios.fields.post_call_mock_responses')}
                      </label>
                      <p className="text-fg-tertiary text-xs">
                        {t('pages.simulations.scenarios.help.post_call_mock_responses')}{' '}
                        {t('pages.simulations.scenarios.help.supports_templates')}
                      </p>
                    </div>
                    <Button
                      type="button"
                      color="secondary"
                      size="sm"
                      onClick={addPostCallMockEntry}
                      iconLeading={PlusIcon}
                    >
                      {t('pages.simulations.scenarios.add_mock')}
                    </Button>
                  </div>

                  {postCallMockEntries.length === 0 ? (
                    <p className="text-fg-tertiary rounded-lg border border-dashed p-4 text-center text-sm">
                      {t('pages.simulations.scenarios.no_post_call_mocks')}
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {postCallMockEntries.map((entry) => (
                        <div
                          key={entry.id}
                          className="bg-bg-secondary rounded-lg border p-3"
                        >
                          <div className="mb-2 flex items-start gap-2">
                            <div className="flex-1">
                              <Input
                                value={entry.toolName}
                                onChange={(val) => updatePostCallMockEntry(entry.id, 'toolName', val)}
                                placeholder={t(
                                  'pages.simulations.scenarios.placeholders.post_call_mock_tool_name'
                                )}
                                size="sm"
                              />
                            </div>
                          <Button
                            color="tertiary"
                            size="sm"
                            onClick={() => removePostCallMockEntry(entry.id)}
                            aria-label={t('resources.generic.delete')}
                            iconLeading={Trash01Icon}
                          />
                          </div>
                          <TextArea
                            value={entry.response}
                            onChange={(val) => updatePostCallMockEntry(entry.id, 'response', val)}
                            placeholder={t('pages.simulations.scenarios.placeholders.mock_template')}
                            autoResize
                            hint={t('pages.simulations.scenarios.help.mock_template_hint')}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Max Turns */}
              <div>
                <Controller
                  name="max_turns"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t('pages.simulations.scenarios.fields.max_turns')}
                      type="number"
                      value={String(field.value ?? 10)}
                      onChange={(val) => field.onChange(val ? Number(val) : 10)}
                      onBlur={field.onBlur}
                    />
                  )}
                />
              </div>

              {/* Priority */}
              <div>
                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <Input
                      label={t('pages.simulations.scenarios.fields.priority')}
                      type="number"
                      value={String(field.value ?? 0)}
                      onChange={(val) => field.onChange(val ? Number(val) : 0)}
                      onBlur={field.onBlur}
                      description={t('pages.simulations.scenarios.help.priority')}
                    />
                  )}
                />
              </div>

              {/* Simulator AI Deployment */}
              <div>
                <Controller
                  name="simulator_ai_deployment_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label={t('pages.simulations.scenarios.fields.simulator_ai_deployment')}
                      selectedKey={field.value ? String(field.value) : ''}
                      onSelectionChange={(key) =>
                        field.onChange(key === '' ? null : Number(key))
                      }
                      options={aiDeploymentOptions}
                      placeholder={t('pages.simulations.scenarios.placeholders.ai_deployment')}
                      description={t('pages.simulations.scenarios.help.simulator_ai_deployment')}
                    />
                  )}
                />
              </div>

              {/* Judge AI Deployment */}
              <div>
                <Controller
                  name="judge_ai_deployment_id"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label={t('pages.simulations.scenarios.fields.judge_ai_deployment')}
                      selectedKey={field.value ? String(field.value) : ''}
                      onSelectionChange={(key) =>
                        field.onChange(key === '' ? null : Number(key))
                      }
                      options={aiDeploymentOptions}
                      placeholder={t('pages.simulations.scenarios.placeholders.ai_deployment')}
                      description={t('pages.simulations.scenarios.help.judge_ai_deployment')}
                    />
                  )}
                />
              </div>
            </div>
          </div>

          <ModalFooter>
            <Button type="button" color="secondary" onClick={onClose}>
              {t('resources.generic.cancel')}
            </Button>
            <Button type="submit" isLoading={isLoading}>
              {isEdit ? t('resources.generic.save') : t('resources.generic.create')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}

/**
 * Parse comma-separated string to array
 */
function parseCommaSeparated(value?: string): string[] | null {
  if (!value || value.trim() === '') return null
  return value
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)
}

/**
 * Parse array field that might come as:
 * - A proper array: ["a", "b"]
 * - A JSON string: "[\"a\", \"b\"]"
 * - The string "null"
 * - Actual null
 */
function parseArrayField(value: string[] | string | null | undefined): string {
  if (value === null || value === undefined || value === 'null') {
    return ''
  }

  // If it's already an array, join it
  if (Array.isArray(value)) {
    return value.join(', ')
  }

  // If it's a string, try to parse it as JSON
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value)
      if (Array.isArray(parsed)) {
        return parsed.join(', ')
      }
    } catch {
      // Not valid JSON, return as-is if not "null"
      return value === 'null' ? '' : value
    }
  }

  return ''
}

/**
 * Parse string field that might come as the string "null" instead of actual null
 */
function parseStringField(value: string | null | undefined): string {
  if (value === null || value === undefined || value === 'null') {
    return ''
  }
  return value
}

/**
 * Parse JSON field (object or array) to a formatted JSON string for editing
 */
function parseJsonField(value: unknown): string {
  if (value === null || value === undefined) {
    return ''
  }
  if (typeof value === 'string') {
    return value
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return ''
  }
}

/**
 * Parse JSON string to object, returns null if empty or invalid
 */
function parseJsonString(value?: string): Record<string, unknown> | null {
  if (!value || value.trim() === '') return null
  try {
    const parsed = JSON.parse(value)
    if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

/**
 * Parse JSON string to array, returns null if empty or invalid
 */
function parseJsonArrayString(value?: string): Array<{ role: string; content: string }> | null {
  if (!value || value.trim() === '') return null
  try {
    const parsed = JSON.parse(value)
    if (Array.isArray(parsed)) {
      return parsed
    }
    return null
  } catch {
    return null
  }
}

/**
 * Parse mock_responses from backend format to form entries
 * All mock responses now use Jinja2 templating by default
 */
function parseMockResponses(mockResponses: MockResponses | null | undefined): MockEntry[] {
  if (!mockResponses || typeof mockResponses !== 'object') {
    return []
  }

  return Object.entries(mockResponses).map(([toolName, config]) => {
    // Handle both legacy format (string or {template: string}) and new format
    if (typeof config === 'string') {
      return {
        id: crypto.randomUUID(),
        toolName,
        response: config,
      }
    } else if (typeof config === 'object' && 'template' in config) {
      return {
        id: crypto.randomUUID(),
        toolName,
        response: config.template,
      }
    }
    return {
      id: crypto.randomUUID(),
      toolName,
      response: typeof config === 'object' ? JSON.stringify(config, null, 2) : String(config),
    }
  })
}

/**
 * Build mock_responses object from form entries
 * All mock responses now use Jinja2 templating by default
 */
function buildMockResponses(entries: MockEntry[]): MockResponses | null {
  const validEntries = entries.filter(
    (entry) => entry.toolName.trim() !== '' && entry.response.trim() !== ''
  )

  if (validEntries.length === 0) {
    return null
  }

  const result: MockResponses = {}
  for (const entry of validEntries) {
    // Always use template format - Jinja2 templating is now always enabled
    result[entry.toolName] = { template: entry.response }
  }

  return result
}

export default ScenarioFormDialog
